export const PAYMENT_POLICY = {
  slug: "payment-policy",
  title: "Payment, Cancellation & Refund Policy",
  role_label: "Payment Policy",
  section_label: "Article",
  company: "NContro",
  short_company: "NContro",
  effective_date: "August 21, 2026",
  version: "2.0",
  intro: [
    'This Payment, Cancellation & Refund Policy ("Policy") governs all payments, deposits, cancellations, refunds, payouts, chargebacks, and related financial transactions conducted through NContro ("Company," "Platform," "we," "our," or "us").',
    "By using the Platform, all Customers and Vendors agree to this Policy.",
  ],
  articles: [
    {
      n: 1,
      title: "Platform Role",
      body: [
        { p: "The Platform is an online marketplace connecting Customers with independent Vendors." },
        { p: "The Company facilitates payment processing but is not the provider of the services purchased through the Platform." },
        { p: "Contracts for services are entered into between Customers and Vendors." },
      ],
    },
    {
      n: 2,
      title: "Payment Processing",
      body: [
        { p: "Payments are securely processed through one or more independent third-party payment processors." },
        { p: "By making or accepting payments through the Platform, Customers and Vendors also agree to the applicable payment processor's terms and conditions." },
        { p: "The Company does not store complete payment card information." },
      ],
    },
    {
      n: 3,
      title: "Deposits",
      body: [
        { p: "Most bookings require a deposit." },
        { p: "The required deposit amount is determined by the Vendor and will be clearly displayed before the Customer confirms the booking." },
        { p: "A booking is not confirmed until:" },
        {
          list: [
            "the proposal is accepted;",
            "the required deposit is successfully processed; and",
            "the Customer receives a booking confirmation through the Platform.",
          ],
        },
      ],
    },
    {
      n: 4,
      title: "Final Payments",
      body: [
        { p: "The Vendor determines the payment schedule for the remaining balance." },
        { p: "Final payment deadlines will be displayed in the accepted proposal." },
        { p: "Failure to pay the remaining balance may result in cancellation of services, subject to the Vendor's cancellation policy." },
      ],
    },
    {
      n: 5,
      title: "Platform Fees",
      body: [
        { p: "The Platform may charge:" },
        {
          list: [
            "service fees;",
            "booking fees;",
            "processing fees;",
            "subscription fees;",
            "premium listing fees;",
            "advertising fees; or",
            "other disclosed fees.",
          ],
        },
        { p: "All applicable fees will be presented before payment whenever required by law." },
      ],
    },
    {
      n: 6,
      title: "Vendor Commissions",
      body: [
        { p: "The Platform retains an agreed commission from eligible bookings before releasing Vendor payouts." },
        { p: "Commission percentages may vary depending on:" },
        {
          list: [
            "Vendor subscription level;",
            "promotional programs;",
            "negotiated agreements; or",
            "Platform pricing schedules.",
          ],
        },
      ],
    },
    {
      n: 7,
      title: "Vendor Payouts",
      body: [
        { p: "Vendor payouts are initiated after funds have been successfully processed and any applicable holding period has expired." },
        { p: "The Company may delay payouts when reasonably necessary to:" },
        {
          list: [
            "investigate fraud;",
            "resolve chargebacks;",
            "investigate customer disputes;",
            "verify Vendor information;",
            "comply with legal obligations; or",
            "address suspected violations of Platform policies.",
          ],
        },
      ],
    },
    {
      n: 8,
      title: "Cancellations",
      body: [
        { p: "Each Vendor establishes its own cancellation policy." },
        { p: "The applicable cancellation policy will be displayed to the Customer before the booking is confirmed." },
        { p: "Customers are responsible for reviewing the Vendor's cancellation policy before completing a booking." },
      ],
    },
    {
      n: 9,
      title: "Customer Cancellations",
      body: [
        { p: "If a Customer cancels a booking, refunds will be determined according to the Vendor's published cancellation policy unless otherwise required by law." },
        { p: "Platform service fees and payment processing fees may be non-refundable unless prohibited by applicable law." },
      ],
    },
    {
      n: 10,
      title: "Vendor Cancellations",
      body: [
        { p: "If a Vendor cancels a confirmed booking, the Company may, in its discretion:" },
        {
          list: [
            "issue a full or partial refund to the Customer;",
            "assist the Customer in locating another Vendor;",
            "adjust Vendor rankings or visibility;",
            "issue account warnings;",
            "suspend the Vendor account; or",
            "terminate the Vendor account for repeated cancellations or material violations of Platform policies.",
          ],
        },
        { p: "The Company reserves the right to determine appropriate Platform-related remedies while recognizing that refund obligations may also depend on the Vendor's contractual commitments and applicable law." },
      ],
    },
    {
      n: 11,
      title: "Refunds",
      body: [
        { p: "Refund eligibility depends upon:" },
        {
          list: [
            "the Vendor's cancellation policy;",
            "the timing of cancellation;",
            "whether services were partially completed;",
            "applicable law;",
            "payment processor requirements; and",
            "any mutually agreed resolution between the Customer and Vendor.",
          ],
        },
        { p: "The Company may facilitate refunds but does not guarantee that every disputed payment will be refunded." },
      ],
    },
    {
      n: 12,
      title: "No-Shows",
      body: [
        { p: "If a Customer fails to appear for a scheduled event without prior notice, refunds shall be governed by the Vendor's cancellation policy." },
        { p: "If a Vendor fails to appear without a lawful or contractually excused reason, the Company may investigate the matter and take actions under the Vendor Agreement." },
      ],
    },
    {
      n: 13,
      title: "Disputes",
      body: [
        { p: "Customers and Vendors are encouraged to communicate directly through the Platform to resolve disputes." },
        { p: "Where appropriate, the Company may facilitate communication or request supporting documentation, but the Company is not obligated to adjudicate or resolve every dispute." },
      ],
    },
    {
      n: 14,
      title: "Chargebacks",
      body: [
        { p: "If a Customer initiates a chargeback, the Company may temporarily delay Vendor payouts while the matter is investigated." },
        { p: "Vendors agree to provide documentation reasonably requested by the Company or the payment processor, including:" },
        {
          list: [
            "contracts;",
            "proposals;",
            "invoices;",
            "communications;",
            "photographs;",
            "receipts;",
            "proof of performance; and",
            "other relevant evidence.",
          ],
        },
        { p: "The Company may recover chargeback-related amounts from future Vendor payouts to the extent permitted by law and the applicable agreements." },
      ],
    },
    {
      n: 15,
      title: "Fraud Prevention",
      body: [
        { p: "The Company reserves the right to suspend payments, delay payouts, or cancel transactions when reasonably necessary to investigate suspected fraud, unauthorized activity, payment abuse, identity theft, or violations of Platform policies." },
      ],
    },
    {
      n: 16,
      title: "Taxes",
      body: [
        { p: "Customers are responsible for applicable taxes associated with their purchases." },
        { p: "Vendors remain solely responsible for reporting and paying any taxes arising from their business operations unless the Company is required by law to collect, remit, or report certain taxes." },
      ],
    },
    {
      n: 17,
      title: "Currency",
      body: [
        { p: "All payments shall be processed in the currency displayed at checkout unless otherwise specified." },
        { p: "Any foreign exchange conversion rates or additional fees imposed by banks or payment providers are the responsibility of the applicable financial institution or user." },
      ],
    },
    {
      n: 18,
      title: "Payment Errors",
      body: [
        { p: "Users shall promptly notify the Company of any suspected payment errors or unauthorized transactions." },
        { p: "The Company will work with its payment processors to investigate reported issues but cannot guarantee the outcome of payment disputes." },
      ],
    },
    {
      n: 19,
      title: "Policy Changes",
      body: [
        { p: "The Company may update this Policy from time to time." },
        { p: "Material changes will become effective upon posting or as otherwise required by applicable law." },
        { p: "Continued use of the Platform after such changes constitutes acceptance of the revised Policy." },
      ],
    },
    {
      n: 20,
      title: "Booking-Specific Policy Governs",
      body: [
        { p: "The cancellation policy, refund policy, deposit terms, and payment schedule presented to and accepted by the Customer in connection with a particular Booking (as recorded in the Booking Agreement) remain the terms governing that Booking." },
        { p: "Later changes by the Vendor to its general cancellation policy, refund policy, deposit requirements, or payment terms do not retroactively alter an existing accepted Booking, unless both Customer and Vendor agree (including through a Booking modification recorded on the Platform) or applicable law requires otherwise." },
      ],
    },
    {
      n: 21,
      title: "Commission Reversal on Refund",
      body: [
        { p: "The Platform commission is applied by the Platform's payment processor at the time of each Customer payment." },
        { p: "Where a Customer payment is fully refunded through the Platform, any corresponding commission collected on the refunded amount is reversed by the payment processor to the extent supported by the processor and applicable law. Where a Customer payment is partially refunded, commission adjustments follow the payment processor's applicable rules and NContro's then-current refund handling." },
        { p: "Where a Customer cancels and the Vendor legitimately retains all or part of a Deposit or other payment under the accepted cancellation policy for that Booking, the Company retains its commission on the amount so retained by the Vendor." },
        { p: "Chargebacks initiated by financial institutions may affect Vendor payouts and commissions; the Company may adjust or recoup commissions and payouts as reasonably necessary to reflect the final outcome of any chargeback or reversal, subject to applicable law." },
      ],
    },
    {
      n: 22,
      title: "Disputes, Chargebacks, and Platform Records",
      body: [
        { p: "NContro may preserve, review, compile, and where reasonably necessary provide relevant Platform records when responding to refunds, payment disputes, chargebacks, cancellations, Vendor no-shows, Customer no-shows, fraud investigations, safety complaints, or compliance investigations." },
        { p: "Relevant records may include the accepted Proposal or Booking record, the applicable cancellation and refund policy, timestamps, payment and refund records, Platform communications, cancellation and no-show records, and uploaded supporting documentation." },
        { p: "NContro may provide relevant information to payment processors, financial institutions, regulators, law enforcement, or other appropriate entities where legally permitted and reasonably necessary, subject to the Company's Privacy Policy and applicable law." },
        { p: "NContro is not a court, arbitrator, insurer, guarantor, or ultimate fact-finder in Customer/Vendor service disputes." },
      ],
    },
    {
      n: 23,
      title: "Contact",
      body: [
        { p: "Questions regarding payments, refunds, cancellations, or this Policy may be directed to:" },
        {
          contact: {
            company: "NContro",
            address: "2000 N Beauregard St #345, Alexandria, VA 22311",
            email: "scottye@ncontro.com",
            phone: "(901) 573-7661",
            website: "https://www.ncontro.com",
          },
        },
      ],
    },
  ],
};
